import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button, DataTable, DefinitionList, EmptyState, Grid, PageHeader, Panel, Skeleton, Stack, StatTile, t, Text,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { OutsideScopeBanner, useDisplayScope } from '../../components/ScopePicker';
import { RefusalPage } from '../../components/Refusal';
import { EnvironmentBadge, ServerStateBadge, serverStateColumn } from '../../components/ServerState';
import { EnvironmentBadges, ObligationBadges } from '../../components/Inventory';
import { ContactsDialog } from '../../components/catalogue/ContactsDialog';
import { PullRequestNotice } from '../../components/catalogue/PullRequestNotice';
import { api, unwrap } from '../../lib/api';
import { useSession } from '../../lib/session';
import { FULL_HEIGHT } from '../../lib/tables';

type Client = maintenance.components['schemas']['ClientDetail'];
type ProjectRow = maintenance.components['schemas']['ProjectRow'];
type ServerRow = maintenance.components['schemas']['ClientServer'];

/**
 * Client page (SPEC-005, ADR-011), DSM « Record with tabs »: four figures,
 * then its projects, dedicated servers, shared servers and technical contacts.
 */
export function ClientPage() {
  const { client = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { enums } = useSession();
  const scope = useDisplayScope();
  const tab = params.get('tab') ?? 'overview';
  const [editing, setEditing] = useState(false);
  const detail = useQuery({
    queryKey: ['client', client, scope],
    queryFn: async () => unwrap(await api.maintenance.GET('/clients/{client}', { params: { path: { client }, query: { scope } } })),
    refetchInterval: 30_000,
    retry: false,
  });

  const crumbs = [{ label: t('nav.clients'), href: '/clients' }, { label: detail.data?.title ?? client }];
  if (detail.error) return <Shell crumbs={crumbs}><RefusalPage error={detail.error} /></Shell>;
  const c = detail.data;
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  const subtitle = c
    ? [
        t('client.projects', { n: c.counts.projects }),
        t('client.dedicated', { n: c.counts.dedicatedServers }),
        ...c.obligations.map(o => label(enums.obligations, o)),
        t('client.team', { team: c.team }),
      ].join(' · ')
    : undefined;

  const header = (
    <PageHeader
      band
      eyebrow={t('page.kind.client')}
      title={c?.title ?? client}
      status={c?.state ? <ServerStateBadge state={c.state} /> : undefined}
      subtitle={subtitle}
      tabs={{
        label: t('client.tabs.label'),
        value: tab,
        onChange: id => setParams(id === 'overview' ? {} : { tab: id }),
        items: (c?.tabs ?? ['overview', 'settings']).map(id => ({ id, label: t(`client.tabs.${id}`) })),
      }}
    />
  );

  const serverColumns = (shared: boolean) => [
    serverStateColumn<ServerRow>(enums, s => ({ state: s.state })),
    { key: 'name', label: t('client.servers.name'), mono: true, width: 220 },
    { key: 'environment', label: t('client.servers.environment'), width: 140, render: (s: ServerRow) => (s.environment ? <EnvironmentBadge environment={s.environment} /> : t('inventory.none')) },
    { key: 'projects', label: t('client.servers.projects'), grow: true, render: (s: ServerRow) => s.projects.join(', ') },
    shared
      ? { key: 'clients', label: t('client.servers.placement'), width: 190, render: (s: ServerRow) => t('project.servers.shared', { n: s.clients }) }
      : { key: 'region', label: t('client.servers.hosting'), width: 190, render: (s: ServerRow) => [s.provider ? label(enums.providers, s.provider) : null, s.region].filter(Boolean).join(' · ') },
  ];
  const contacts = (list: Client['contacts']) => (
    list.length === 0
      ? <Text variant="body-sm" tone="mute">{t('client.contacts.none')}</Text>
      : <DefinitionList items={list.map(x => ({ key: `${x.role}-${x.email}`, label: t(`client.contacts.roles.${x.role}`), value: `${x.name} · ${x.email}${x.mobile ? ` · ${x.mobile}` : ''}` }))} />
  );

  return (
    <Shell crumbs={crumbs} header={header}>
      {!c ? (
        <Skeleton lines={8} />
      ) : (
        <Stack gap={24}>
          <OutsideScopeBanner kind="client" outside={c.outsideDisplayScope} />
          {tab === 'overview' && (
            <>
              <Grid min={150}>
                <StatTile label={t('client.tiles.projects')} value={c.counts.projects} />
                <StatTile label={t('client.tiles.dedicated')} value={c.counts.dedicatedServers} />
                <StatTile label={t('client.tiles.shared')} value={c.counts.sharedServers} />
                <StatTile label={t('client.tiles.openIssues')} value={c.counts.openIssues} note={t('client.tiles.openIssuesNote')} />
              </Grid>
              <Panel title={t('client.projectsPanel')} flush>
                <DataTable
                  bordered={false}
                  height={FULL_HEIGHT}
                  label={t('client.projectsPanel')}
                  rows={c.projects}
                  rowKey="name"
                  toolbar={false}
                  density="compact"
                  onRowClick={(p: ProjectRow) => navigate(`/projects/${encodeURIComponent(p.name)}`)}
                  columns={[
                    serverStateColumn<ProjectRow>(enums, p => ({ state: p.state }), { noProduction: true }),
                    { key: 'title', label: t('projects.columns.name'), grow: true },
                    { key: 'applications', label: t('projects.columns.applications'), type: 'number' as const, width: 120 },
                    { key: 'environments', label: t('projects.columns.environments'), width: 320, render: (p: ProjectRow) => <EnvironmentBadges environments={p.environments} /> },
                    { key: 'obligations', label: t('projects.columns.obligations'), width: 220, render: (p: ProjectRow) => <ObligationBadges obligations={p.obligations} /> },
                  ]}
                  empty={<EmptyState framed={false} icon="folder" title={t('client.noProject')} />}
                />
              </Panel>
              <Panel title={t('client.dedicatedPanel')} flush>
                <DataTable
                  bordered={false}
                  height={FULL_HEIGHT}
                  label={t('client.dedicatedPanel')}
                  rows={c.dedicatedServers}
                  rowKey="name"
                  toolbar={false}
                  density="compact"
                  onRowClick={(s: ServerRow) => navigate(`/servers/${encodeURIComponent(s.name)}`)}
                  columns={serverColumns(false)}
                  empty={<EmptyState framed={false} icon="dns" title={t('client.noDedicated')} />}
                />
              </Panel>
              <Panel title={t('client.sharedPanel')} subtitle={t('client.sharedSubtitle')} flush>
                <DataTable
                  bordered={false}
                  height={FULL_HEIGHT}
                  label={t('client.sharedPanel')}
                  rows={c.sharedServers}
                  rowKey="name"
                  toolbar={false}
                  density="compact"
                  onRowClick={(s: ServerRow) => navigate(`/servers/${encodeURIComponent(s.name)}`)}
                  columns={serverColumns(true)}
                  empty={<EmptyState framed={false} icon="dns" title={t('client.noShared')} />}
                />
              </Panel>
              <Panel title={t('client.contacts.title')}>{contacts(c.contacts)}</Panel>
            </>
          )}
          {tab === 'settings' && <PullRequestNotice pullRequest={c.pullRequests.contacts} />}
          {tab === 'settings' && (
            <Panel
              title={t('client.settings.title')}
              actions={c.actions.includes('client.update') ? <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>{t('client.settings.editContacts')}</Button> : undefined}
            >
              <Stack gap={16}>
                {contacts(c.contacts)}
                <DefinitionList items={[{ label: t('client.settings.vault'), value: c.vault, mono: true }]} emptyText={t('client.settings.noVault')} />
              </Stack>
            </Panel>
          )}
        </Stack>
      )}
      {c && editing && <ContactsDialog open onClose={() => setEditing(false)} client={{ name: c.name, title: c.title }} current={c.contacts as Parameters<typeof ContactsDialog>[0]['current']} />}
    </Shell>
  );
}
